import { useState } from 'react';
import type { Editor } from '@tiptap/react';
import Icon from 'components/editor/GridDndEditor/TiptapEditor/components/Icon';
import IcoArrowDropDownRounded from 'components/icon/icons/IcoArrowDropDownRounded';
import PyMuiMenu from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenu';
import PyMuiMenuItem from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuItem';
import PyMuiMenuLabel from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiMenuLabel';
import PyMuiButton from 'components/editor/GridDndEditor/TiptapEditor/components/generalComponents/PyMuiButton';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import type { EmailTemplateEventType } from 'pages/settings/email-templates/types';

export type EmailTemplateToolbarOption = {
  html: string;
  id: string;
  name: string;
  subject: string;
  templateId?: string;
  eventType: EmailTemplateEventType;
};

type EmailTemplatePickerButtonProps = {
  editor: Editor | null;
  options: EmailTemplateToolbarOption[];
  disabled?: boolean;
  onSelect: (option: EmailTemplateToolbarOption) => void;
};

const BUTTON_WIDTH_PX = 168;

const EmailTemplatePickerButton = ({ editor, options, disabled = false, onSelect }: EmailTemplatePickerButtonProps) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedEmailTemplateName, setSelectedEmailTemplateName] = useState<string | undefined>(undefined);

  const handleOpenMenu: React.MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleSelect = (option: EmailTemplateToolbarOption) => {
    setSelectedEmailTemplateName(option.name);
    onSelect(option);
    editor?.commands.focus();
    handleClose();
  };

  const defaultLabel = t('settings.email_templates.title');
  const selectedLabel = selectedEmailTemplateName ?? defaultLabel;
  const isDisabled = disabled || !editor || !options.length;

  return (
    <Stack direction="row" alignItems="center">
      <PyMuiButton
        width={BUTTON_WIDTH_PX}
        testId="email-template-picker-button"
        isMenuOpen={Boolean(anchorEl)}
        disabled={isDisabled}
        onClickAction={handleOpenMenu}
      >
        <PyMuiMenuLabel data-testid="email-template-picker-label" style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {selectedLabel}
        </PyMuiMenuLabel>
        <Icon>
          <IcoArrowDropDownRounded />
        </Icon>
      </PyMuiButton>

      <PyMuiMenu
        testId="email-template-picker-menu"
        id="email-template-picker-menu"
        menuListProps="email-template-picker-menu"
        anchorEl={anchorEl}
        isMenuOpen={Boolean(anchorEl)}
        handleClose={handleClose}
      >
        {options.map((option) => (
          <PyMuiMenuItem
            key={option.id}
            testId={`email-template-picker-item-${option.id.replace(/[/\s]/g, '-')}`}
            onClick={() => handleSelect(option)}
            menuItem={
              <Tooltip title={option.name} placement="left">
                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {option.name}
                </Box>
              </Tooltip>
            }
          />
        ))}
      </PyMuiMenu>
    </Stack>
  );
};

export default EmailTemplatePickerButton;
